import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { ShepherdService } from 'angular-shepherd';

import { createTourSteps, ROUTE_TO_FEATURE_MAP, StepsFeature } from '../constants/steps-tour.constants';

@Injectable({
  providedIn: 'root'
})
export class GuidedTourService {
  private tourSteps: ReturnType<typeof createTourSteps>;

  constructor(
    private shepherdService: ShepherdService,
    private router: Router
  ) {
    // Cria os steps com a referência correta do shepherdService
    this.tourSteps = createTourSteps(this.shepherdService);

    this.configureShepherdDefaults();
  }

  /**
   * Inicia o tour baseado na rota atual
   */
  public startTourForCurrentRoute(): void {
    const currentRoute = this.router.url;
    const feature = this.getFeatureFromRoute(currentRoute);

    if (feature && this.hasStepsForFeature(feature)) {
      this.startTour(feature);
    }
  }

  /**
   * Inicia o tour para uma feature específica
   */
  public startTour(feature: StepsFeature): void {
    const steps = this.tourSteps[feature];
    if (steps && steps.length > 0) {
      this.shepherdService.addSteps(steps as any);
      this.shepherdService.start();
    }
  }

  /**
   * Inicia o tour com steps customizados
   */
  public startCustomTour(steps: Array<any>): void {
    if (steps && steps.length > 0) {
      this.shepherdService.addSteps(steps);
      this.shepherdService.start();
    }
  }

  /**
   * Determina a feature baseada na rota
   */
  private getFeatureFromRoute(route: string): StepsFeature | null {
    // Remove query parameters e fragments
    const cleanRoute = route.split('?')[0].split('#')[0];

    // Normaliza a rota (remove trailing slash, exceto para root)
    const normalizedRoute = cleanRoute === '/' ? '/' : cleanRoute.replace(/\/$/, '');

    // Verifica correspondência exata primeiro
    if (ROUTE_TO_FEATURE_MAP[normalizedRoute]) {
      return ROUTE_TO_FEATURE_MAP[normalizedRoute];
    }

    // Define padrões de rota mais específicos
    const routePatterns: Array<{ pattern: RegExp, feature: StepsFeature }> = [
      { pattern: /^\/cotar(-produtos)?($|\/|$)/, feature: 'cotar-produtos' },
      // { pattern: /^\/midas($|\/|$)/, feature: 'midas' },
      { pattern: /^\/home($|\/|$)/, feature: 'home' },
      { pattern: /^\/$/, feature: 'home' }
    ];

    // Verifica padrões em ordem de especificidade
    for (const { pattern, feature } of routePatterns) {
      if (pattern.test(normalizedRoute)) {
        return feature;
      }
    }

    return null;
  }

  /**
   * Verifica se existe tour para a rota atual
   */
  public hasTourForCurrentRoute(): boolean {
    const currentRoute = this.router.url;
    const feature = this.getFeatureFromRoute(currentRoute);

    // ✅ RETORNA FALSE se não encontrar feature mapeada
    return feature !== null &&
      this.tourSteps[feature] &&
      this.tourSteps[feature].length > 0;
  }

  /**
   * Obtém os steps para uma feature específica
   */
  public getStepsForFeature(feature: StepsFeature): any[] {
    return this.tourSteps[feature] || [];
  }

  /**
   * Obtém todas as features disponíveis
   */
  public getAvailableFeatures(): StepsFeature[] {
    return Object.keys(this.tourSteps) as StepsFeature[];
  }

  /**
   * Verifica se uma feature específica tem tour disponível
   */
  public hasStepsForFeature(feature: StepsFeature): boolean {
    return this.tourSteps[feature] && this.tourSteps[feature].length > 0;
  }

  /**
   * Para o tour atual
   */
  public stopTour(): void {
    if (this.shepherdService.isActive) {
      this.shepherdService.cancel();
    }
  }

  /**
   * Completa o tour atual
   */
  public completeTour(): void {
    if (this.shepherdService.isActive) {
      this.shepherdService.complete();
    }
  }

  /**
   * Verifica se há um tour ativo
   */
  public isTourActive(): boolean {
    return this.shepherdService.isActive;
  }

  /**
   * Vai para o próximo step
   */
  public nextStep(): void {
    if (this.shepherdService.isActive) {
      this.shepherdService.next();
    }
  }

  /**
   * Volta para o step anterior
   */
  public previousStep(): void {
    if (this.shepherdService.isActive) {
      this.shepherdService.back();
    }
  }

  /**
   * Configura as opções padrão do Shepherd
   */
  public configureShepherdDefaults(options?: any): void {
    const defaultOptions = {
      classes: 'shadow-md bg-purple-dark',
      scrollTo: false,
      cancelIcon: {
        enabled: true,
      },
      ...options
    };

    this.shepherdService.defaultStepOptions = defaultOptions;
    this.shepherdService.modal = true;
    this.shepherdService.confirmCancel = false;
  }

  /**
   * Adiciona listeners para eventos do tour
   */
  public addTourEventListeners(): void {
    this.shepherdService.tourObject?.on('complete', () => {
      this.onTourComplete();
    });

    this.shepherdService.tourObject?.on('cancel', () => {
      this.onTourCancel();
    });

    this.shepherdService.tourObject?.on('show', (event: any) => {
      this.onStepShow(event);
    });
  }

  /**
   * Callback quando o tour é completado
   */
  private onTourComplete(): void {
    const currentRoute = this.router.url;
    const feature = this.getFeatureFromRoute(currentRoute);

    if (feature) {
      // Salva no localStorage que o tour foi completado para esta feature
      localStorage.setItem(`tour-completed-${feature}`, 'true');
    }
  }

  /**
   * Callback quando o tour é cancelado
   */
  private onTourCancel(): void {
    // Lógica adicional quando o tour é cancelado
    // console.log('Tour cancelado');
  }

  /**
   * Callback quando um step é mostrado
   */
  private onStepShow(event: any): void {
    // Lógica adicional quando um step é mostrado
    // console.log('Step mostrado:', event);
  }

  /**
   * Verifica se o tour já foi completado para uma feature
   */
  public isTourCompletedForFeature(feature: StepsFeature): boolean {
    return localStorage.getItem(`tour-completed-${feature}`) === 'true';
  }

  /**
   * Marca o tour como completado para uma feature
   */
  public markTourAsCompleted(feature: StepsFeature): void {
    localStorage.setItem(`tour-completed-${feature}`, 'true');
  }

  /**
   * Remove a marcação de tour completado para uma feature
   */
  public resetTourCompletion(feature: StepsFeature): void {
    localStorage.removeItem(`tour-completed-${feature}`);
  }

  /**
   * Verifica se deve mostrar o tour automaticamente
   */
  public shouldShowTourForCurrentRoute(): boolean {
    const currentRoute = this.router.url;
    const feature = this.getFeatureFromRoute(currentRoute);

    if (!feature) return false;

    return this.hasStepsForFeature(feature) && !this.isTourCompletedForFeature(feature);
  }
}
